import { api } from '../api';

declare const Hls: any;

export interface AnimeSourceRaw {
  /** Посилання на чужий плеєр (напр. ashdi.vip/embed/...). */
  u: string;
  /** true — типове/дефолтне джерело для цієї серії (обирається першим). */
  d: boolean;
}

export function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** Декодує ChapterItem.path anime-серії (base64url JSON {n, s}) назад у
 * список джерел — той самий payload, що на сервері вже розбирає
 * anitube.ts:parseChapter, тільки тут це робимо БЕЗ мережевого запиту,
 * щоб побудувати перелік доступних плеєрів для ВСІХ серій одразу (для
 * кроку "Оберіть плеєр"). */
export function decodeChapterSources(chapterPath: string): AnimeSourceRaw[] {
  try {
    const b64 = chapterPath.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    const payload = JSON.parse(json) as { s?: AnimeSourceRaw[] };
    return Array.isArray(payload.s) ? payload.s.filter((s) => s && typeof s.u === 'string') : [];
  } catch {
    return [];
  }
}

/** Обирає найвищу доступну якість. "AUTO" лишається фолбеком лише тоді, коли
 * інших (числових) варіантів немає — так само, як у anitube-web. */
function pickBestQuality(qualities: Record<string, string>): string | null {
  const keys = Object.keys(qualities || {});
  if (!keys.length) return null;
  let best = keys[0];
  let bestValue = -Infinity;
  for (const key of keys) {
    const m = key.match(/(\d+)/);
    const value = m ? parseInt(m[1], 10) : -1;
    if (value > bestValue) {
      bestValue = value;
      best = key;
    }
  }
  return best;
}

function formatTime(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export interface PlaybackProgress {
  currentTime: number;
  duration: number;
  /** Скільки реально "переглянутих" секунд минуло з попередньої події (0,
   * якщо відео на паузі/перемотується) — саме цю дельту слід накопичувати
   * для лічильника "22+ хвилини", а не годинникові секунди сторінки. */
  watchedDeltaSeconds: number;
}

export interface MountAnimePlayerOptions {
  /** hostname, обраний користувачем на кроці "Оберіть плеєр" (див.
   * anime.ts). Якщо серед джерел ЦІЄЇ серії є джерело з таким hostname —
   * стартуємо саме з нього; інакше тихо фолбечимо на типове (d===true). */
  preferredHost?: string | null;
  /** Позиція в секундах, з якої серію можна продовжити (з попереднього
   * перегляду) — якщо задано й це має сенс (>5с і не в останніх ~5с відео),
   * відео НЕ стартує одразу: спочатку поверх плеєра з'являється напівпрозорий
   * оверлей із двома кнопками — "Продовжити з X:XX" і "Почати з початку". */
  resumeSeconds?: number | null;
  /** Викликається на кожен timeupdate/pause реального <video> (не для
   * iframe-фолбеку — там ми не маємо доступу до подій чужого плеєра). */
  onProgress?: (progress: PlaybackProgress) => void;
}

export interface AnimePlayerHandle {
  /** Зупиняє відтворення (hls.js-інстанс тощо) — обов'язково викликати
   * перед повторним монтуванням, інакше попередня серія продовжить
   * вантажити дані вхолосту. */
  destroy: () => void;
  /** Поточна позиція реального <video> (null, якщо зараз показано iframe-
   * фолбек — там позиція невідома). */
  getPosition: () => { currentTime: number; duration: number } | null;
}

/**
 * Монтує в `container` вбудований плеєр для списку альтернативних джерел
 * ОДНІЄЇ серії: пігулки-перемикачі "плеєр" (за hostname, з badge "типове"
 * для джерела з d===true) над відеообластю — клік по пігулці резолвить і
 * відтворює обране джерело звичайним <video> (mp4/hls.js) або, якщо для
 * хостингу немає екстрактора / він впав, фолбеком через <iframe>.
 */
export function mountAnimePlayer(container: HTMLElement, sources: AnimeSourceRaw[], options: MountAnimePlayerOptions = {}): AnimePlayerHandle {
  const { preferredHost, resumeSeconds, onProgress } = options;

  container.innerHTML = '';
  container.classList.add('anime-video');

  if (!sources.length) {
    container.innerHTML = '<p class="anime-player-hint">Для цієї серії не знайдено жодного джерела відео.</p>';
    return { destroy: () => {}, getPosition: () => null };
  }

  let hls: any = null;
  let destroyed = false;
  let lastTickTime: number | null = null;
  // Позиція, яку ще треба запропонувати продовжити — обнуляється одразу
  // після першого показу оверлею (вибрав користувач "продовжити" чи
  // "спочатку" — байдуже), щоб не питати повторно при перемиканні
  // якості/плеєра всередині тієї самої серії.
  let pendingResumeSeconds = resumeSeconds && resumeSeconds > 5 ? resumeSeconds : null;

  const frame = document.createElement('div');
  frame.className = 'anime-video-frame';

  const videoEl = document.createElement('video');
  videoEl.controls = true;
  videoEl.playsInline = true;
  videoEl.hidden = true;

  const iframeEl = document.createElement('iframe');
  iframeEl.allowFullscreen = true;
  iframeEl.loading = 'lazy';
  iframeEl.referrerPolicy = 'no-referrer';
  iframeEl.hidden = true;

  const statusEl = document.createElement('div');
  statusEl.className = 'anime-video-status';
  statusEl.hidden = true;

  // Напівпрозорий оверлей "Продовжити з X:XX" / "Почати з початку" —
  // поверх плеєра, показується ЗАМІСТЬ автозапуску, поки не обрано.
  const resumeOverlay = document.createElement('div');
  resumeOverlay.className = 'anime-video-resume-overlay';
  resumeOverlay.hidden = true;

  frame.appendChild(videoEl);
  frame.appendChild(iframeEl);
  frame.appendChild(statusEl);
  frame.appendChild(resumeOverlay);
  container.appendChild(frame);

  const qualityBar = document.createElement('div');
  qualityBar.className = 'anime-video-quality-bar';
  qualityBar.hidden = true;
  container.appendChild(qualityBar);

  let playersRow: HTMLElement | null = null;
  if (sources.length > 1) {
    playersRow = document.createElement('div');
    playersRow.className = 'anime-video-players';
    container.appendChild(playersRow);
  }

  // ---- реальний прогрес відтворення (не годинник сторінки) ----
  // ВАЖЛИВО: destroy() викликає videoEl.pause(), що асинхронно генерує
  // подію 'pause' ВЖЕ "мертвого" відео. Без цієї перевірки той 'pause'
  // прилітав би вже ПІСЛЯ того, як anime.ts перемкнув currentChapter/
  // accumulatedSeconds на НАСТУПНУ серію — і затирав би їх даними
  // старого відео (звідси баг: попередня серія "не запам'ятовує" 92%,
  // а наступна помилково позначається переглянутою).
  function reportProgress(watchedDeltaSeconds: number) {
    if (destroyed) return;
    onProgress?.({
      currentTime: videoEl.currentTime || 0,
      duration: videoEl.duration || 0,
      watchedDeltaSeconds,
    });
  }

  videoEl.addEventListener('play', () => {
    lastTickTime = videoEl.currentTime;
  });
  videoEl.addEventListener('pause', () => {
    lastTickTime = videoEl.currentTime;
    reportProgress(0);
  });
  videoEl.addEventListener('timeupdate', () => {
    const cur = videoEl.currentTime;
    let delta = 0;
    // Рахуємо "переглянутим" лише плавний хід часу вперед під час
    // відтворення (0 < delta < 2с) — це відсікає як паузу (timeupdate під
    // час паузи не викликається), так і перемотки/зациклення, які інакше
    // накрутили б лічильник миттєво.
    if (!videoEl.paused && lastTickTime !== null) {
      const d = cur - lastTickTime;
      if (d > 0 && d < 2) delta = d;
    }
    lastTickTime = cur;
    reportProgress(delta);
  });

  function hideResumeOverlay() {
    resumeOverlay.hidden = true;
    resumeOverlay.innerHTML = '';
  }

  /** Викликається, коли відео готове до відтворення (metadata завантажена).
   * Якщо є що запропонувати продовжити — показує оверлей і НЕ запускає
   * відео сама; інакше просто стартує відтворення одразу. */
  function onReadyToPlay() {
    const resumeAt = pendingResumeSeconds;
    const duration = videoEl.duration;
    const resumeIsValid = resumeAt !== null && (!Number.isFinite(duration) || duration <= 0 || resumeAt < duration - 5);

    if (!resumeIsValid) {
      pendingResumeSeconds = null;
      videoEl.play().catch(() => {});
      return;
    }

    resumeOverlay.innerHTML = '';
    const box = document.createElement('div');
    box.className = 'anime-video-resume-box';

    const continueBtn = document.createElement('button');
    continueBtn.type = 'button';
    continueBtn.className = 'anime-video-resume-btn primary';
    continueBtn.textContent = `Продовжити з ${formatTime(resumeAt!)}`;
    continueBtn.addEventListener('click', () => {
      pendingResumeSeconds = null;
      hideResumeOverlay();
      videoEl.currentTime = resumeAt!;
      videoEl.play().catch(() => {});
    });

    const restartBtn = document.createElement('button');
    restartBtn.type = 'button';
    restartBtn.className = 'anime-video-resume-btn';
    restartBtn.textContent = 'Почати з початку';
    restartBtn.addEventListener('click', () => {
      pendingResumeSeconds = null;
      hideResumeOverlay();
      videoEl.currentTime = 0;
      videoEl.play().catch(() => {});
    });

    box.appendChild(continueBtn);
    box.appendChild(restartBtn);
    resumeOverlay.appendChild(box);
    resumeOverlay.hidden = false;
  }

  function setStatus(message: string | null, type?: 'error') {
    if (!message) {
      statusEl.hidden = true;
      statusEl.textContent = '';
      return;
    }
    statusEl.hidden = false;
    statusEl.textContent = message;
    statusEl.className = 'anime-video-status' + (type ? ' ' + type : '');
  }

  function stopPlayback() {
    if (hls) {
      hls.destroy();
      hls = null;
    }
    hideResumeOverlay();
    videoEl.pause();
    videoEl.removeAttribute('src');
    videoEl.load();
    videoEl.hidden = true;
    iframeEl.hidden = true;
    iframeEl.src = '';
    qualityBar.hidden = true;
    qualityBar.innerHTML = '';
    lastTickTime = null;
  }

  function playHls(url: string) {
    videoEl.hidden = false;
    if (typeof Hls !== 'undefined' && Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(url);
      hls.attachMedia(videoEl);
      // Відео НЕ запускаємо одразу тут — onReadyToPlay сам вирішить, чи
      // стартувати відразу, чи спершу показати оверлей "Продовжити".
      hls.on(Hls.Events.MANIFEST_PARSED, () => onReadyToPlay());
      hls.on(Hls.Events.ERROR, (_evt: unknown, data: any) => {
        if (data?.fatal) setStatus('Помилка відтворення HLS: ' + data.type, 'error');
      });
    } else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari вміє нативно
      videoEl.src = url;
      videoEl.addEventListener('loadedmetadata', () => onReadyToPlay(), { once: true });
    } else {
      setStatus('Цей браузер не підтримує відтворення HLS.', 'error');
    }
  }

  function playMp4(url: string) {
    videoEl.hidden = false;
    videoEl.src = url;
    videoEl.addEventListener('loadedmetadata', () => onReadyToPlay(), { once: true });
  }

  function playIframe(url: string) {
    iframeEl.hidden = false;
    iframeEl.src = url;
  }

  function renderQualityBar(qualities: Record<string, string>, kind: string, activeQuality: string | null, onPick: (q: string) => void) {
    const keys = Object.keys(qualities);
    if (kind === 'iframe' || keys.length <= 1) {
      qualityBar.hidden = true;
      qualityBar.innerHTML = '';
      return;
    }
    qualityBar.hidden = false;
    qualityBar.innerHTML = '';
    keys.forEach((q) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = q;
      if (q === activeQuality) btn.classList.add('active');
      btn.addEventListener('click', () => onPick(q));
      qualityBar.appendChild(btn);
    });
  }

  async function playSource(source: AnimeSourceRaw, btn?: HTMLElement) {
    if (playersRow) {
      playersRow.querySelectorAll('.anime-video-player-btn.active').forEach((b) => b.classList.remove('active'));
      btn?.classList.add('active');
    }

    stopPlayback();
    setStatus('Шукаю пряме посилання на відео…');

    let data;
    try {
      data = await api.resolveVideo(source.u);
    } catch {
      setStatus(null);
      playIframe(source.u);
      return;
    }
    if (destroyed) return;

    if (data.status !== 'ok' || !data.kind || !data.qualities) {
      // немає екстрактора під цей хостинг або він впав — фолбек на iframe плеєра
      setStatus(null);
      playIframe(source.u);
      return;
    }

    setStatus(null);

    const startPlayback = (quality: string) => {
      stopPlayback();
      const url = data.qualities![quality];
      if (data.kind === 'hls') playHls(url);
      else if (data.kind === 'mp4') playMp4(url);
      else playIframe(url);
      renderQualityBar(data.qualities!, data.kind!, quality, startPlayback);
    };

    const defaultQuality = pickBestQuality(data.qualities);
    if (defaultQuality) startPlayback(defaultQuality);
    else playIframe(source.u);
  }

  const buttons: HTMLElement[] = [];
  if (playersRow) {
    sources.forEach((source) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'anime-video-player-btn';
      const label = document.createElement('span');
      label.textContent = hostnameOf(source.u);
      btn.appendChild(label);
      if (source.d) {
        const badge = document.createElement('span');
        badge.className = 'badge';
        badge.textContent = 'типове';
        btn.appendChild(badge);
      }
      btn.addEventListener('click', () => playSource(source, btn));
      playersRow!.appendChild(btn);
      buttons.push(btn);
    });
  }

  const preferred = preferredHost ? sources.find((s) => hostnameOf(s.u) === preferredHost) : undefined;
  const primary = preferred || sources.find((s) => s.d) || sources[0];
  playSource(primary, buttons[sources.indexOf(primary)]);

  return {
    destroy: () => {
      destroyed = true;
      stopPlayback();
    },
    getPosition: () => {
      if (videoEl.hidden || !Number.isFinite(videoEl.currentTime)) return null;
      return { currentTime: videoEl.currentTime, duration: videoEl.duration || 0 };
    },
  };
}

/** Парсить data-anime-sources із контейнера, який повернув anime-плагін
 * (parseChapter) у полі content — див. src/plugins/sources/ukrainian/anitube.ts. */
export function parseAnimeSources(html: string): AnimeSourceRaw[] | null {
  const wrap = document.createElement('div');
  wrap.innerHTML = html;
  const el = wrap.querySelector<HTMLElement>('[data-anime-sources]');
  if (!el) return null;
  try {
    const raw = JSON.parse(el.getAttribute('data-anime-sources') || '[]');
    if (!Array.isArray(raw)) return null;
    return raw.filter((s): s is AnimeSourceRaw => s && typeof s.u === 'string');
  } catch {
    return null;
  }
}
